/**
 * Админ-раздел (§16.5, экран /admin): статус синхронизации, карантин
 * нераспознанных строк, отчёт руководителю по расхождениям с best_client.
 *
 * Доступ разный по ролям (§16.4): статус синхронизации и карантин — только
 * администратору, отчёт по расхождениям — руководителю и администратору.
 * Решение о доступе принимает бэкенд (§16.2): каждый раздел грузится
 * отдельно и при 403 просто не показывается, не ломая остальную страницу.
 */

import { useEffect, useState } from 'react'
import { SessionExpired, api } from '../api/client'
import type {
  OutcomeBankRow,
  FeedbackList,
  RegistryReport,
  ReviewRule,
  MatchReport,
  QuarantineList,
  RosterEntry,
  SyncChanges,
  SyncRunResult,
  SyncStatus,
} from '../api/types'
import { formatDateTime, formatMoney } from '../utils/format'

/** Единая обработка ошибок по секции: сессия истекла — переход в портал
 * (решение о доступе принимает бэкенд, §16.2); иначе — текст ошибки на
 * месте своей секции, не ломая остальную страницу. */
function sectionError(err: unknown): string {
  if (err instanceof SessionExpired) {
    window.location.href = err.portalUrl
    return ''
  }
  return err instanceof Error ? err.message : String(err)
}

function SyncStatusCard() {
  const [status, setStatus] = useState<SyncStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [runResult, setRunResult] = useState<SyncRunResult | null>(null)

  function load() {
    api.adminSyncStatus()
      .then(setStatus)
      .catch((err) => setError(sectionError(err)))
  }

  useEffect(load, [])

  async function runNow() {
    setRunning(true)
    setRunResult(null)
    try {
      setRunResult(await api.adminSyncRun())
      load()
    } catch (err) {
      setError(sectionError(err))
    } finally {
      setRunning(false)
    }
  }

  if (error != null) return <section className="card"><p className="muted">{error}</p></section>

  return (
    <section className="card">
      <div className="card-row">
        <h2 style={{ margin: 0 }}>Статус синхронизации таблицы</h2>
        <button disabled={running} onClick={runNow}>
          {running ? 'Обновляю…' : 'Обновить сейчас'}
        </button>
      </div>

      {status == null ? (
        <p className="muted">Загружаю…</p>
      ) : status.предупреждение != null ? (
        <p className="muted">{status.предупреждение}</p>
      ) : (
        <dl className="admin-facts">
          <div><dt>Снимок от</dt><dd>{formatDateTime(status.снят) ?? '—'}</dd></div>
          <div><dt>Вкладок разобрано</dt><dd>{status['вкладок разобрано']}</dd></div>
          <div><dt>Условий распознано</dt><dd>{status.условий}</dd></div>
          <div><dt>В карантине</dt><dd>{status['в карантине']}</dd></div>
          {status['работает на предыдущем снимке'] === true && (
            <div className="notice small">Работает на предыдущем снимке — последняя синхронизация не опубликована</div>
          )}
          {status['почему не опубликован'] != null && (
            <div className="notice small">Причина: {status['почему не опубликован']}</div>
          )}
          {status['требуют проверки (старше 30 дней)'] != null &&
            status['требуют проверки (старше 30 дней)'].length > 0 && (
            <div>
              <dt>Требуют проверки (старше 30 дней)</dt>
              <dd>{status['требуют проверки (старше 30 дней)'].join(', ')}</dd>
            </div>
          )}
          {status['новые вкладки без правил'] != null && status['новые вкладки без правил'].length > 0 && (
            <div>
              <dt>Новые вкладки без правил</dt>
              <dd>{status['новые вкладки без правил'].join(', ')}</dd>
            </div>
          )}
          {status.ошибки != null && status.ошибки.length > 0 && (
            <div><dt>Ошибки</dt><dd className="error">{status.ошибки.join('; ')}</dd></div>
          )}
          {status['программы требуют сверки с нормативным актом'] != null &&
            status['программы требуют сверки с нормативным актом'].length > 0 && (
            <div>
              <dt>Программы требуют сверки с нормативным актом (§12)</dt>
              <dd>{status['программы требуют сверки с нормативным актом'].join(', ')}</dd>
            </div>
          )}
        </dl>
      )}

      {runResult != null && (
        <p className="muted">
          {runResult.опубликован ? 'Опубликовано' : 'Не опубликовано'} · вкладок: {runResult.вкладок} ·{' '}
          {runResult.длительность_сек} с
          {runResult.причина != null && ` · ${runResult.причина}`}
        </p>
      )}
    </section>
  )
}

function ChangesCard() {
  const [changes, setChanges] = useState<SyncChanges | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.adminSyncChanges().then(setChanges).catch((err) => setError(sectionError(err)))
  }, [])

  if (error != null) return null
  if (changes == null || changes.изменений === 0) return null

  return (
    <details className="card">
      <summary><h2 style={{ display: 'inline' }}>Изменения с прошлой синхронизации ({changes.изменений})</h2></summary>
      <ul>
        {changes.список.map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ul>
    </details>
  )
}

function QuarantineCard() {
  const [quarantine, setQuarantine] = useState<QuarantineList | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.adminQuarantine().then(setQuarantine).catch((err) => setError(sectionError(err)))
  }, [])

  if (error != null) return <section className="card"><p className="muted">{error}</p></section>

  return (
    <section className="card">
      <h2>Карантин нераспознанных строк</h2>
      {quarantine == null ? (
        <p className="muted">Загружаю…</p>
      ) : quarantine.записей === 0 ? (
        <p className="muted">Пусто — все строки разобраны.</p>
      ) : (
        <>
          <p className="muted">Не участвуют в подборе, требуют ручной проверки: {quarantine.записей}</p>
          <div className="table-scroll">
            <table className="service-table">
              <thead>
                <tr>
                  <th>Вкладка</th><th>Банк</th><th>Программа</th><th>Параметр</th>
                  <th>Исходный текст</th><th>Причина</th>
                </tr>
              </thead>
              <tbody>
                {quarantine.список.map((row, index) => (
                  <tr key={index}>
                    <td>{row.вкладка}</td>
                    <td>{row.банк ?? '—'}</td>
                    <td>{row.программа ?? '—'}</td>
                    <td>{row.параметр ?? '—'}</td>
                    <td>{row['исходный текст']}</td>
                    <td>{row.причина}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}

function MatchReportCard() {
  const [report, setReport] = useState<MatchReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.adminMatchReport().then(setReport).catch((err) => setError(sectionError(err)))
  }, [])

  if (error != null) return <section className="card"><p className="muted">{error}</p></section>

  return (
    <section className="card">
      <h2>Отчёт по расхождениям с best_client</h2>
      <p className="muted">
        Доля сделок, где рекомендация разошлась с лучшим для клиента вариантом, и средняя
        переплата клиента по таким сделкам (§15.4).
      </p>

      {report == null ? (
        <p className="muted">Загружаю…</p>
      ) : report.записей === 0 ? (
        <p className="muted">{report.предупреждение ?? 'Расчётов пока не было.'}</p>
      ) : (
        <dl className="admin-facts">
          <div><dt>Всего расчётов</dt><dd>{report['всего расчётов']}</dd></div>
          <div>
            <dt>Расхождений с best_client</dt>
            <dd className={report['порог доли превышен'] ? 'error' : ''}>
              {report['расхождений с best_client']} ({Math.round(Number(report['доля расхождений']) * 100)}%)
              {report['порог доли превышен'] && ` — выше порога ${Math.round(Number(report['порог доли расхождений']) * 100)}%`}
            </dd>
          </div>
          {report['средняя переплата клиента по расхождениям'] != null && (
            <div>
              <dt>Средняя переплата клиента по расхождениям</dt>
              <dd className={report['порог переплаты превышен'] ? 'error' : ''}>
                {formatMoney(report['средняя переплата клиента по расхождениям'])}
                {report['порог переплаты превышен'] &&
                  ` — выше порога ${formatMoney(report['порог средней переплаты'])}`}
              </dd>
            </div>
          )}
        </dl>
      )}
    </section>
  )
}

const ROLE_LABELS: Record<string, string> = {
  broker: 'брокер', manager: 'руководитель', admin: 'администратор',
}

function RosterCard() {
  const [entries, setEntries] = useState<RosterEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState('broker')
  const [busy, setBusy] = useState(false)

  function load() {
    api.adminRoster().then(setEntries).catch((err) => setError(sectionError(err)))
  }

  useEffect(load, [])

  async function addEntry(event: React.FormEvent) {
    event.preventDefault()
    if (!fullName.trim() || !phone.trim()) return
    setBusy(true)
    try {
      await api.adminRosterAdd({ full_name: fullName.trim(), phone: phone.trim(), role })
      setFullName('')
      setPhone('')
      setRole('broker')
      load()
    } catch (err) {
      setError(sectionError(err))
    } finally {
      setBusy(false)
    }
  }

  async function removeEntry(phoneToRemove: string) {
    setBusy(true)
    try {
      await api.adminRosterRemove(phoneToRemove)
      load()
    } catch (err) {
      setError(sectionError(err))
    } finally {
      setBusy(false)
    }
  }

  if (error != null) return <section className="card"><p className="muted">{error}</p></section>

  return (
    <section className="card">
      <h2>Сотрудники — вход по ФИО и телефону</h2>
      <p className="muted">
        Только эти сотрудники могут войти в приложение. Уволенного — просто удалите из списка.
      </p>

      <form onSubmit={addEntry} className="roster-form">
        <input
          type="text" placeholder="ФИО" value={fullName}
          onChange={(e) => setFullName(e.target.value)} required
        />
        <input
          type="tel" placeholder="Рабочий телефон" value={phone}
          onChange={(e) => setPhone(e.target.value)} required
        />
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="broker">Брокер</option>
          <option value="manager">Руководитель</option>
          <option value="admin">Администратор</option>
        </select>
        <button className="primary" type="submit" disabled={busy}>Добавить</button>
      </form>

      {entries == null ? (
        <p className="muted">Загружаю…</p>
      ) : entries.length === 0 ? (
        <p className="muted">Список пуст — добавьте первого сотрудника выше.</p>
      ) : (
        entries.map((entry) => (
          <div key={entry.телефон} className="card-row roster-row">
            <div>
              <strong>{entry.фио}</strong>
              <div className="muted">
                {entry.телефон} · {ROLE_LABELS[entry.роль] ?? entry.роль}
              </div>
            </div>
            <button disabled={busy} onClick={() => removeEntry(entry.телефон)}>Удалить</button>
          </div>
        ))
      )}
    </section>
  )
}

/** Обратная связь от специалистов и брокеров (D-FB-01): разбирает директор. */
/** Журнал действий сотрудников (ТЗ 3.0 §32): кто, когда и что сделал. */
function AuditCard() {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.adminAudit>> | null>(null)
  const [action, setAction] = useState('')
  const [employee, setEmployee] = useState('')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    api
      .adminAudit({ action, employee })
      .then(setData)
      .catch((err) => setError(sectionError(err)))
  }, [action, employee])
  if (error != null) return null // раздел только для администратора
  if (data == null) return null
  return (
    <section className="card">
      <h2>Журнал действий</h2>
      <p className="muted">
        Кто и когда входил, считал, выгружал PDF клиенту, менял правила и список сотрудников.
        Персональных данных клиентов в журнале нет.
      </p>
      <div className="actions">
        <select value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">все действия</option>
          {Object.entries(data.действия).map(([code, label]) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </select>
        <input placeholder="Сотрудник" value={employee} onChange={(e) => setEmployee(e.target.value)} />
      </div>
      {data.записи.length === 0 ? (
        <p className="muted">Записей нет.</p>
      ) : (
        <table className="trace-table">
          <thead>
            <tr>
              <th>Когда</th>
              <th>Сотрудник</th>
              <th>Действие</th>
              <th>Анкета / объект</th>
              <th>Подробности</th>
            </tr>
          </thead>
          <tbody>
            {data.записи.map((r) => (
              <tr key={r.id}>
                <td>{new Date(r.когда).toLocaleString('ru-RU')}</td>
                <td>{r.сотрудник ?? '—'}</td>
                <td>{r.действие}</td>
                <td>{r.объект ? r.объект.slice(0, 12) : '—'}</td>
                <td>
                  {Object.entries(r.подробности)
                    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${Array.isArray(v) ? v.join(', ') : String(v)}`)
                    .join('; ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

/** Результаты заявок по банкам (ТЗ 3.0 §28): статистика, не правило. */
function OutcomesCard() {
  const [rows, setRows] = useState<OutcomeBankRow[] | null>(null)
  const [note, setNote] = useState('')
  const [check, setCheck] = useState<{
    проверено: number
    потери: { анкета: string; банк: string; программа: string | null; 'почему система не нашла': string[] }[]
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    api
      .adminOutcomes()
      .then((data) => {
        setRows(data['по банкам'])
        setNote(data.оговорка)
        setCheck(data['сверка с расчётом'])
      })
      .catch((err) => setError(sectionError(err)))
  }, [])
  if (error != null) return <section className="card"><p className="muted">{error}</p></section>
  if (rows == null) return null
  return (
    <section className="card">
      <h2>Результаты заявок по банкам</h2>
      <p className="muted">{note}</p>
      {check != null && check.проверено > 0 && (
        <div className={check.потери.length > 0 ? 'error' : 'muted'}>
          Сверка одобрений с текущим расчётом: проверено {check.проверено},{' '}
          {check.потери.length === 0
            ? 'система находит все одобрившие банки.'
            : `система не находит ${check.потери.length} — это потери на реальных случаях:`}
          {check.потери.length > 0 && (
            <ul>
              {check.потери.map((loss) => (
                <li key={`${loss.анкета}-${loss.банк}`}>
                  {loss.банк}
                  {loss.программа ? `, ${loss.программа}` : ''} (анкета {loss.анкета.slice(0, 8)}):{' '}
                  {loss['почему система не нашла'].join('; ')}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {rows.length === 0 ? (
        <p className="muted">Результатов пока нет — брокеры отмечают их на странице результата.</p>
      ) : (
        <table className="trace-table">
          <thead>
            <tr>
              <th>Банк</th>
              <th>Заявок</th>
              <th>Одобрено</th>
              <th>Отказ</th>
              <th>Сделок</th>
              <th>Доля одобрений</th>
              <th>Ставка: факт − расчёт</th>
              <th>Частые причины отказа</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.банк}>
                <td>{r.банк}</td>
                <td>{r.заявок}</td>
                <td>{r.одобрено}</td>
                <td>{r.отказ}</td>
                <td>{r.сделок}</td>
                <td>{r['доля одобрений'] ?? '—'}</td>
                <td>{r['ставка: факт минус расчёт, п.п.'] ?? '—'}</td>
                <td>{r['частые причины отказа'].join('; ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function FeedbackCard() {
  const [data, setData] = useState<FeedbackList | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<string>('new')
  const [notes, setNotes] = useState<Record<number, string>>({})

  function load() {
    api.adminFeedback(filter || undefined).then(setData).catch((err) => setError(sectionError(err)))
  }
  useEffect(load, [filter])

  async function resolve(id: number, status: string) {
    try {
      await api.adminFeedbackResolve(id, status, notes[id] ?? '')
      load()
    } catch (err) {
      setError(sectionError(err))
    }
  }

  if (error != null) return <section className="card"><p className="muted">{error}</p></section>
  if (data == null) return null
  return (
    <section className="card">
      <h2>Обратная связь от брокеров ({data.новых} новых)</h2>
      <p className="muted">
        Подтверждённое замечание становится правилом и проверкой, чтобы ошибка не вернулась.
      </p>
      <div className="actions">
        {[['new', 'новые'], ['accepted', 'подтверждённые'], ['', 'все']].map(([value, label]) => (
          <button key={value} className={filter === value ? 'primary' : ''} onClick={() => setFilter(value)}>
            {label}
          </button>
        ))}
      </div>
      {data.замечания.length === 0 ? (
        <p className="muted">Замечаний нет.</p>
      ) : (
        data.замечания.map((item) => (
          <div key={item.id} className="feedback-item">
            <div>
              <strong>{item.вид}</strong>
              {item.банк != null && <> · {item.банк}</>}
              {item.программа != null && <> · {item.программа}</>}
              <span className="muted"> · {formatDateTime(item.создано)} · {item.сотрудник} · анкета {item.анкета.slice(0, 8)}</span>
            </div>
            <div>{item.текст}</div>
            <div className="muted">Статус: {item['статус текстом']}{item.решение ? ` — ${item.решение}` : ''}</div>
            {item.статус === 'new' && (
              <div className="actions">
                <input
                  placeholder="Комментарий (необязательно)"
                  value={notes[item.id] ?? ''}
                  onChange={(e) => setNotes({ ...notes, [item.id]: e.target.value })}
                />
                <button onClick={() => resolve(item.id, 'accepted')}>Подтвердить</button>
                <button onClick={() => resolve(item.id, 'rejected')}>Не подтвердилось</button>
              </div>
            )}
            {item.статус === 'accepted' && (
              <div className="actions">
                <button onClick={() => resolve(item.id, 'done')}>Исправлено</button>
              </div>
            )}
          </div>
        ))
      )}
    </section>
  )
}

/** Одно правило на проверке: утвердить (со своим значением) или отклонить. */
function ReviewRuleRow({ rule, onDone }: { rule: ReviewRule; onDone: () => void }) {
  const [low, setLow] = useState(rule.нижняя ?? '')
  const [high, setHigh] = useState(rule.верхняя ?? '')
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function decide(decision: 'approve' | 'reject') {
    setBusy(true)
    setError(null)
    try {
      await api.adminRegistryDecide({
        rule_id: rule.rule_id, решение: decision, нижняя: low || null, верхняя: high || null,
        комментарий: comment || null,
      })
      onDone()
    } catch (err) {
      setError(sectionError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="feedback-item">
      <div>
        <strong>{rule.банк}</strong> · {rule.программа ?? 'без программы'} · {rule.параметр}
      </div>
      <div>«{rule['текст ячейки']}»</div>
      <div className="muted">{rule.источник}</div>
      <div className="muted">Почему на проверке: {rule['почему на проверке']}</div>
      <div className="actions">
        <input placeholder="от" value={low} onChange={(e) => setLow(e.target.value)} style={{ width: 120 }} />
        <input placeholder="до" value={high} onChange={(e) => setHigh(e.target.value)} style={{ width: 120 }} />
        <input placeholder="Комментарий" value={comment} onChange={(e) => setComment(e.target.value)} />
        <button disabled={busy} onClick={() => decide('approve')}>Утвердить</button>
        <button disabled={busy} onClick={() => decide('reject')}>Не условие</button>
      </div>
      {error != null && <p className="error">{error}</p>}
    </div>
  )
}

/** Проверка правил из таблицы (D-REG-01): очередь на проверку и противоречия вкладок. */
function RegistryCard() {
  const [data, setData] = useState<RegistryReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  function load() {
    api.adminRegistry().then(setData).catch((err) => setError(sectionError(err)))
  }
  useEffect(load, [])

  async function choose(ruleId: string) {
    try {
      await api.adminRegistryDecide({ rule_id: ruleId, решение: 'approve', комментарий: 'выбрано из расходящихся вкладок' })
      load()
    } catch (err) {
      setError(sectionError(err))
    }
  }

  if (error != null) return <section className="card"><p className="muted">{error}</p></section>
  if (data == null) return null
  if (data.предупреждение != null) return <section className="card"><p className="muted">{data.предупреждение}</p></section>
  const review = data['на проверке'] ?? []
  const conflicts = data['противоречия вкладок'] ?? []
  const decided = data['решения директора'] ?? []
  return (
    <section className="card">
      <h2>Проверка правил из таблицы</h2>
      <p className="muted">
        Правил в реестре: {data.правил}. Однозначные действуют сразу; здесь — то, что требует вашего решения.
        Решение действует, пока текст ячейки в таблице не изменится.
        {data['устаревших решений'] ? ` Устаревших решений: ${data['устаревших решений']} — ячейки изменились.` : ''}
      </p>
      <details open={review.length > 0}>
        <summary><h3 style={{ display: 'inline' }}>На проверке ({review.length})</h3></summary>
        {review.map((rule) => (
          <ReviewRuleRow key={rule.rule_id} rule={rule} onDone={load} />
        ))}
      </details>
      <details>
        <summary><h3 style={{ display: 'inline' }}>Вкладки расходятся ({conflicts.length})</h3></summary>
        {conflicts.map((conflict, index) => (
          <div key={`${conflict.банк}-${conflict.параметр}-${index}`} className="feedback-item">
            <div><strong>{conflict.банк}</strong> · {conflict.программа} · {conflict.параметр}</div>
            {conflict.варианты.map((variant) => (
              <div key={variant.rule_id} className="actions">
                <span>
                  {variant['в расчёте'] ? '✔ в расчёте: ' : ''}«{variant['текст ячейки'].slice(0, 160)}»
                  <span className="muted"> — {variant.источник}</span>
                </span>
                {!variant['в расчёте'] && (
                  <button onClick={() => choose(variant.rule_id)}>Это верное</button>
                )}
              </div>
            ))}
          </div>
        ))}
      </details>
      {decided.length > 0 && (
        <details>
          <summary><h3 style={{ display: 'inline' }}>Ваши решения ({decided.length})</h3></summary>
          <ul>
            {decided.map((item) => (
              <li key={item.rule_id}>
                {item.банк}, {item.программа}, {item.параметр}: «{item['текст ячейки'].slice(0, 100)}» —{' '}
                {item.решение?.решение === 'reject' ? 'не условие' : 'утверждено'}
                {item.решение?.верхняя ? ` (до ${item.решение.верхняя})` : ''}
                {item.решение && !item.решение.действует ? ' — ячейка изменилась, нужно решить заново' : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

export function AdminPage() {
  return (
    <div>
      <h1 style={{ fontSize: 18 }}>Админ-раздел</h1>
      <OutcomesCard />
      <AuditCard />
      <FeedbackCard />
      <RegistryCard />
      <RosterCard />
      <MatchReportCard />
      <SyncStatusCard />
      <ChangesCard />
      <QuarantineCard />
    </div>
  )
}
